// One chip, close up: the selected TensorCore's listing on top, following a cursor;
// the state of its units; a detail panel for whatever the listing cannot say; and the
// chip's rows on a timeline across the bottom. Both UIs open this page, the dashboard
// on a double-click and the drill-down from its map.

import { el, fmtPs, fmtInt } from "./dom.js";
import { Timeline } from "./timeline.js";
import { Detail } from "./detail.js";
import { Listing } from "./listing.js";
import { UnitBoxes } from "./unitboxes.js";
import { rowKeyOf } from "./links.js";
import { openGuide } from "./guide.js";

export class ChipPage {
  constructor(root, ctx, opts = {}) {
    this.ctx = ctx;
    this.model = ctx.model;
    this.store = ctx.store;
    this.opts = opts;           // {onBack, onChange}
    this.root = root;
    this.chip = null;
    this.tc = null;            // the selected stream's key
    this.cursor = NaN;
    this.playing = 0;
    this.offs = [];
    this.active = false;
  }

  destroy() {
    this.stop();
    for (const off of this.offs) off();
    this.offs = [];
    if (this.tl) { this.tl.destroy(); this.tl = null; }
    this.root.textContent = "";
    this.active = false;
  }

  redraw() { if (this.tl) this.tl.invalidate(3); }

  // Show `chip`, with `tc` selected for the listing, the cursor at `t`, `item` selected.
  open(chip, tc = null, t = null, item = null) {
    const m = this.model;
    this.destroy();
    this.active = true;
    this.chip = chip;
    const cores = m.coresOfChip(chip);
    const streams = m.streamsOfChip(chip);
    // `tc` is a stream key, or a TensorCore index for old links.
    const want = typeof tc === "number" ? `c${chip}t${tc}` : tc;
    this.tc = want != null && streams.some((c) => c.key === want) ? want : (streams[0] ? streams[0].key : null);
    const root = this.root;

    const bar = el("div", "drill-bar");
    bar.appendChild(el("span", "strong", m.chipLabel(chip)));
    const prev = el("button", null, "prev chip"), next = el("button", null, "next chip");
    prev.onclick = () => this.open(m.chipIds[(m.chipIds.indexOf(chip) - 1 + m.chipIds.length) % m.chipIds.length], this.tc);
    next.onclick = () => this.open(m.chipIds[(m.chipIds.indexOf(chip) + 1) % m.chipIds.length], this.tc);
    bar.appendChild(prev); bar.appendChild(next);
    const fit = el("button", null, "fit"); fit.onclick = () => this.tl.fitAll(); bar.appendChild(fit);
    if (this.opts.brush) {
      const zb = el("button", null, "zoom to window"); zb.title = "the window brushed on the slice"; zb.onclick = () => { const [a, b] = this.opts.brush(); this.tl.setView(a, b); }; bar.appendChild(zb);
    }
    const play = el("button", null, "play"); play.title = "step through the selected core's bundles (space)"; play.onclick = () => this.togglePlay(); bar.appendChild(play);
    this.playBtn = play;
    this.cursorLbl = el("span", "dim cursor");
    bar.appendChild(this.cursorLbl);
    // Only what the guide does not already say: the keys are in it, under "?".
    bar.appendChild(el("span", "dim help", "click sets the cursor, drag its line to sweep"));
    const help = el("button", "guide-open", "?");
    help.title = "how to read this page: the colours, the rows, the keys (?)";
    help.onclick = () => openGuide();
    bar.appendChild(help);
    this.tabBtns = new Map();
    if (streams.length) {
      bar.appendChild(el("span", "dim", "listing of"));
      for (const c of streams) {
        if (c.kind === "tile" && c.n === 0) continue;
        const b = el("button", c.key === this.tc ? "on" : "", c.kind === "tile" ? `t${c.tile}` : c.short);
        b.title = c.label;
        b.onclick = () => this.selectTc(c.key);
        bar.appendChild(b);
        this.tabBtns.set(c.key, b);
      }
    } else bar.appendChild(el("span", "dim", "no core ran a program on this chip"));
    root.appendChild(bar);

    const stack = el("div", "drill-stack");
    root.appendChild(stack);
    // What the listing's columns are, above it and not inside: its rows are virtual
    // and absolutely positioned, and a header among them would be painted over.
    const lhead = el("div", "lhead mono");
    const hpc = el("span", "lpc", "pc"); hpc.title = "the program counter of this bundle";
    const hn = el("span", "lcount", "×n"); hn.title = "how many times this core issued that pc";
    const hi = el("span", null, "instructions, one per slot");
    lhead.appendChild(hpc); lhead.appendChild(hn); lhead.appendChild(hi);
    stack.appendChild(lhead);
    const listEl = el("div", "drill-listing");
    this.listing = new Listing(listEl, this.ctx, { onPick: (core, pc) => this.jumpToPc(core, pc) });
    stack.appendChild(listEl);
    const ubEl = el("div");
    this.units = new UnitBoxes(ubEl, this.ctx);
    stack.appendChild(ubEl);
    const tlEl = el("div", "drill-tl");
    stack.appendChild(tlEl);
    this.tl = new Timeline(tlEl, this.ctx, {
      onSelect: (hit, tt) => this.onSelect(hit, tt),
      onCursor: (tt) => { this.cursor = tt; this.followCursor(); this.changed(); },
      onView: () => this.changed(),
    });
    const rows = [{ id: `c${chip}host`, kind: "host", chip, label: "host", title: "firmware commands, doorbells and interrupts" }];
    for (const c of streams) {
      if (c.kind === "tile" && c.n === 0) continue;
      const tiles = c.kind === "sc" ? m.tilesOf(chip, c.ci).length : 0;
      rows.push({ id: c.key, kind: "tc", chip, ci: c.ci, core: c, label: c.kind === "tile" ? `tile ${c.tile}` : c.short, depth: c.kind === "tile" ? 2 : 1, title: `${c.label}: ${c.n.toLocaleString()} bundles`, click: () => this.selectTc(c.key), height: c.kind === "tile" ? 20 : tiles ? 44 : 34 });
      if (c.kind === "tile") continue;
      if (c.units.length) rows.push({ id: c.key + ".units", kind: "tc.units", chip, core: c, label: "units", depth: 2 });
      if ((this.ctx.links.dmaByIssuer.get(c.key) || []).length) rows.push({ id: c.key + ".dma", kind: "tc.dma", chip, core: c, label: "dma", depth: 2 });
    }
    const streamed = new Set(m.scCoresOfChip(chip).map((c) => c.ci));
    for (const ci of m.scsOfChip(chip)) if (!streamed.has(ci)) rows.push({ id: `c${chip}s${ci}`, kind: "sc", chip, ci, label: `SC${ci}`, title: "SparseCore: dispatches, waits, flag writes, DMAs (no bundle stream)" });
    void cores;
    this.tl.setRows(rows);
    this.tl.fitAll();

    this.selectTc(this.tc);
    if (t != null) { this.setCursor(t); this.tl.zoomTo(t - 200 * m.defaultCycle(), t + 200 * m.defaultCycle(), 0); }
    if (item) this.select(item);
    this.offs.push(this.store.on("hlRow", (s) => this.tl.highlightRow(s.hlRow)));
    this.changed();
  }

  changed() { if (this.opts.onChange) this.opts.onChange(); }

  selectTc(key) {
    this.tc = typeof key === "number" ? `c${this.chip}t${key}` : key;
    const core = this.core;
    for (const [k, b] of this.tabBtns) b.classList.toggle("on", k === this.tc);
    this.listing.setCore(core);
    this.units.setCore(core);
    this.tl.highlightRow(core ? core.key : null);
    this.followCursor();
    this.changed();
  }

  get core() { return this.model.coreByKey.get(this.tc) || null; }

  setCursor(t) {
    this.cursor = t;
    this.tl.setCursor(t);
    this.followCursor();
    this.changed();
  }

  followCursor() {
    const core = this.core;
    if (!core || Number.isNaN(this.cursor)) { this.cursorLbl.textContent = Number.isNaN(this.cursor) ? "" : fmtPs(this.cursor); return; }
    const bi = this.model.bundleAt(core, this.cursor);
    this.listing.follow(bi);
    this.units.at(bi);
    const regs = bi >= 0 ? this.model.regsAtStep(core, core.step[bi]) : null;
    this.cursorLbl.textContent = `${fmtPs(this.cursor)}${bi >= 0 ? `  ·  pc ${core.pc[bi]}, step ${fmtInt(core.step[bi])}, cycle ${fmtInt(this.model.cycAt(core, bi))}` : ""}${regs ? "  ·  " + regs : ""}`;
  }

  step(dir) {
    const core = this.core;
    if (!core || !core.n) return;
    let bi = Number.isNaN(this.cursor) ? 0 : this.model.bundleAt(core, this.cursor) + dir;
    bi = Math.max(0, Math.min(core.n - 1, bi));
    this.store.set({ selection: null });
    this.setCursor(core.t[bi]);
    const x = this.tl.x(core.t[bi]);
    if (x < 0 || x > this.tl.W) { const span = this.tl.t1 - this.tl.t0; this.tl.setView(core.t[bi] - span / 2, core.t[bi] + span / 2); }
  }

  togglePlay() {
    if (this.playing) this.stop();
    else { this.playing = setInterval(() => this.step(1), 80); this.playBtn.textContent = "pause"; }
  }
  stop() { if (this.playing) clearInterval(this.playing); this.playing = 0; if (this.playBtn) this.playBtn.textContent = "play"; }

  jumpToPc(core, pc, nearest = false) {
    const occ = this.model.pcOccurrences(core, pc);
    const cur = Number.isNaN(this.cursor) ? -1 : this.model.bundleAt(core, this.cursor);
    let nxt = occ.find((k) => k > cur) ?? occ[0];
    if (nearest) nxt = occ.reduce((best, k) => (Math.abs(k - cur) < Math.abs(best - cur) ? k : best), occ[0]);
    if (nxt == null) return;
    this.store.set({ selection: null });
    this.setCursor(core.t[nxt]);
    this.tl.zoomTo(core.t[nxt] - 30 * core.cyclePs, core.t[nxt] + 30 * core.cyclePs, 0);
  }

  onSelect(hit, t) {
    this.tl.setArrows([]);
    // A click lands the listing on the stream the item belongs to, so the bundle at
    // the cursor is the one that issued it: the wait, the flag write, the DMA.
    const core = !hit ? null : hit.kind === "bundle" ? hit.core : this.model.coreByKey.get(String(hit.row).split(".")[0]) || null;
    if (hit && hit.kind === "bundle") { this.store.set({ selection: null }); this.cursor = hit.core.t[hit.bi]; }
    else this.cursor = t;
    // The detail panel first: it takes its height from the listing, whose scroll to
    // the followed row must see the final layout.
    this.showDetail(hit);
    if (core && core.key !== this.tc) this.selectTc(core.key); else this.followCursor();
    this.changed();
  }

  select(item) {
    this.store.set({ selection: item });
    this.tl.setArrows([]);
    this.showDetail(item);
    this.tl.invalidate(2);
  }

  // The chip page has no detail panel: the listing, the unit boxes and the timeline
  // say what there is to say, and the dashboard keeps the panel for the rest.
  showDetail(item) { void item; }

  arrowFor(w, r) {
    const toRow = rowKeyOf(w.chip, w.core, w.ci);
    const fromT = r.how === "dma" ? r.dma.end : r.flag ? r.flag.at : w.t1;
    let fromRow = r.fromRow;
    if (r.how === "dma" && r.dma.byKind === "tc") fromRow = `c${r.dma.chip}t${r.dma.byCi}.dma`;
    this.tl.setArrows([{ fromRow, fromT, toRow, toT: w.open ? this.model.tEnd : w.t1, fromLabel: r.how === "dma" ? `DMA #${r.dma.id}` : r.how === "core" ? r.core.label : fromRow }]);
  }

  // Returns true when the key was taken.
  onKey(e) {
    if (!this.active || !this.tl) return false;
    const tl = this.tl, span = tl.t1 - tl.t0;
    const k = e.key;
    if (k === "ArrowLeft") { if (e.shiftKey) tl.setView(tl.t0 - span * 0.1, tl.t1 - span * 0.1); else this.step(-1); }
    else if (k === "ArrowRight") { if (e.shiftKey) tl.setView(tl.t0 + span * 0.1, tl.t1 + span * 0.1); else this.step(1); }
    // Up and down walk the listing: the cursor goes to the execution of the
    // neighbouring pc nearest in time to where it is.
    else if (k === "ArrowUp" || k === "ArrowDown") { e.preventDefault(); const pc = this.listing.neighbourPc(k === "ArrowUp" ? -1 : 1); if (pc != null && this.core) this.jumpToPc(this.core, pc, true); }
    else if (k === " ") this.togglePlay();
    else if (k === "+" || k === "=") tl.zoomAt(tl.W / 2, 0.5);
    else if (k === "-") tl.zoomAt(tl.W / 2, 2);
    else if (k === "f") tl.fitAll();
    else if (k === "Escape") { this.store.set({ selection: null }); tl.setArrows([]); this.followCursor(); }
    else if (k === "Backspace") { if (this.opts.onBack) this.opts.onBack(); }
    else return false;
    e.preventDefault();
    return true;
  }

  toHash() {
    const out = { chip: String(this.chip), tc: this.tc == null ? "" : this.tc };
    if (!Number.isNaN(this.cursor)) out.t = String(Math.round(this.cursor));
    if (this.tl) out.w = `${Math.round(this.tl.t0)}-${Math.round(this.tl.t1)}`;
    return out;
  }
}
